import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal.jsx';
import { useUiStore } from '@/state/uiStore.js';
import { loadSearchItems, buildResultGroups, activateSearchItem } from './universalSearch.js';
import SearchResultsList from './SearchResultsList.jsx';

/**
 * Quick Search modal (Ctrl+K / title-bar trigger) — cache-led autocomplete
 * across users, groups, shared mailboxes, file servers and printers. Shares
 * its engine (universalSearch.js) and result rendering (SearchResultsList)
 * with the inline Dashboard search bar.
 *
 * Keyboard: ↑/↓ move the selection, Enter opens it, Esc closes.
 */
const PER_GROUP_LIMIT = 6;

export default function GlobalSearchModal() {
  const open = useUiStore((s) => s.globalSearchOpen);
  const initialQuery = useUiStore((s) => s.globalSearchInitialQuery);
  const onClose = useUiStore((s) => s.closeGlobalSearch);

  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [value, setValue] = useState('');
  const [items, setItems] = useState(/** @type {any[]} */ ([]));
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);

  // On open: seed the query, focus, and load the (shared) cache.
  useEffect(() => {
    if (!open) return undefined;
    setValue(initialQuery || '');
    setActive(0);
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    let cancelled = false;
    setStatus('loading');
    setError('');
    loadSearchItems()
      .then((list) => { if (!cancelled) { setItems(list); setStatus('ready'); } })
      .catch((err) => { if (!cancelled) { setError(err?.message || 'Search is unavailable right now.'); setStatus('error'); } });
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, initialQuery]);

  const groups = useMemo(() => buildResultGroups(items, value, PER_GROUP_LIMIT), [items, value]);
  const flat = useMemo(() => groups.flatMap((g) => g.shown), [groups]);
  useEffect(() => { setActive(0); }, [value]);

  function activate(item) {
    if (!item) return;
    activateSearchItem(item);
    onClose();
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); activate(flat[active]); }
  }

  if (!open) return null;

  return (
    <Modal open onClose={onClose} className="modal--global-search" ariaLabel="Quick search" width="560px">
      <div className="global-search">
        <div
          className="global-search__header"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '-16px -16px 0',
            borderRadius: '12px 12px 0 0'
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'white' }}>
              <i className="fas fa-search" aria-hidden="true" /> Quick Search
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
              Search users, groups, devices, drives, shares &amp; more
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', fontSize: '18px'
            }}
            aria-label="Close search"
          >
            &times;
          </button>
        </div>

        <div
          className="global-search__input"
          style={{
            display: 'flex', alignItems: 'center', padding: '16px 20px',
            background: 'var(--surface-2)', gap: '12px',
            borderBottom: '1px solid var(--hairline)', margin: '0 -16px'
          }}
        >
          <i className="fas fa-search" style={{ color: 'var(--accent)', fontSize: '18px' }} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            id="globalSearchInput"
            placeholder="Type a name or email..."
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '15px', color: 'var(--ink)', background: 'transparent' }}
            aria-label="Search input"
          />
          {status === 'loading' && <i className="fas fa-spinner fa-spin" style={{ color: 'var(--faint)' }} aria-hidden="true" />}
        </div>

        <div
          id="globalSearchResults"
          className="global-search__results"
          style={{ maxHeight: '360px', overflowY: 'auto', padding: '8px', margin: '0 -16px' }}
        >
          {status === 'error' && (
            <Prompt icon="fa-triangle-exclamation" tone="var(--bad)">{error}</Prompt>
          )}
          {status !== 'error' && !value.trim() && (
            <Prompt icon="fa-keyboard">
              {status === 'loading' ? 'Loading directory…' : 'Start typing to search users, groups, devices, mailboxes, servers and printers.'}
            </Prompt>
          )}
          {status === 'ready' && value.trim() && flat.length === 0 && (
            <Prompt icon="fa-magnifying-glass">No matches for “{value.trim()}”.</Prompt>
          )}
          {value.trim() && (
            <SearchResultsList groups={groups} active={active} onHover={setActive} onActivate={activate} />
          )}
        </div>

        <div
          className="global-search__footer"
          style={{
            padding: '12px 20px', background: 'var(--surface-2)',
            borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'center',
            gap: '20px', fontSize: '12px', color: 'var(--muted)', margin: '0 -16px -16px', borderRadius: '0 0 12px 12px'
          }}
        >
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </Modal>
  );
}

function Prompt({ icon, tone, children }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: tone || 'var(--muted)', fontSize: '14px' }}>
      <i className={`fas ${icon}`} style={{ fontSize: '24px', display: 'block', marginBottom: '12px', opacity: 0.7 }} aria-hidden="true" />
      {children}
    </div>
  );
}
